/* eslint-disable react/forbid-component-props */
import {Document, Link, Text, View} from '@react-pdf/renderer';
import {memo} from 'react';
import {type cardPaymentProps} from '../../../../../types/cardPaymentServiceDetails.js';
import {IMAGES} from '../../../../../utils/constants/image.js';
import {
  SCHEME_MASTERCARD,
  SCHEME_VISA,
} from '../../../../../utils/constants/public/psr.js';
import getPsrLabel from '../../../../../utils/getPsrLabel.js';
import {
  getCardRatePercent,
  getPercent,
  getQuotationPercent,
  getQuotationPrice,
  getTotalCardRatePercent,
  getTotalPercent,
} from '../../../../../utils/quotation.js';
import {
  ApplyNowCTA,
  Banner,
  CallToActionHeader,
  CallToActionWrapper,
  ClockIcon,
  Header,
  ListItem,
  LloydsLogo,
  MouseIcon,
  Page,
  Paragraph,
  Phone,
  PhoneIcon,
  PSRLogo,
  Question,
  QuoteNumber,
  ServiceName,
  Strong,
  Sup,
  Table,
  Td,
  TextWithSup,
  TextWrapper,
  Th,
  ThinBorder,
  Title,
  Tr,
} from './style.js';

const QuoteDocument = memo((props: cardPaymentProps) => {
  const quoteReference = props?.quoteResponse?.quote?.quoteReference;
  const schemeRates = props.quoteResponse.quote?.schemeRates;
  const authorisationFee =
    props?.quoteResponse.quote?.otherTransactionFees?.authorisationFee;
  const chargeBackFee =
    props?.quoteResponse.quote?.otherTransactionFees?.chargeBackFee;
  const revivalFee =
    props?.quoteResponse.quote?.otherTransactionFees?.revivalFee;

  const mastercardRates = schemeRates?.find(
    (rate) => rate.schemeName === SCHEME_MASTERCARD
  );
  const visaRates = schemeRates?.find(
    (rate) => rate.schemeName === SCHEME_VISA
  );
  const mdFace = getQuotationPercent(mastercardRates?.debit?.face);
  const mcFace = getQuotationPercent(mastercardRates?.credit?.face);
  const mbFace = getQuotationPercent(mastercardRates?.business?.face);
  const vdFace = getQuotationPercent(visaRates?.debit?.face);
  const vcFace = getQuotationPercent(visaRates?.credit?.face);
  const vbFace = getQuotationPercent(visaRates?.business?.face);
  const mdPhoneOnline = getQuotationPercent(
    mastercardRates?.debit?.online || mastercardRates?.debit?.moto
  );
  const mcPhoneOnline = getQuotationPercent(
    mastercardRates?.credit?.online || mastercardRates?.credit?.moto
  );
  const mbPhoneOnline = getQuotationPercent(
    mastercardRates?.business?.online || mastercardRates?.business?.moto
  );
  const vdPhoneOnline = getQuotationPercent(
    visaRates?.debit?.online || visaRates?.debit?.moto
  );
  const vcPhoneOnline = getQuotationPercent(
    visaRates?.credit?.online || visaRates?.credit?.moto
  );
  const vbPhoneOnline = getQuotationPercent(
    visaRates?.business?.online || visaRates?.business?.moto
  );

  const monthlyCharges = props?.quoteResponse?.quote?.otherMonthlyFees;
  const mmsc =
    props?.quoteResponse?.quote?.otherTransactionFees
      ?.minimumMonthlyServiceCharge;
  const gateway = monthlyCharges?.gateway;
  const transactionValue = gateway?.transaction?.value
    ? getTotalPercent(gateway?.transaction?.value)
    : '0p';
  const pos = monthlyCharges?.pos;
  const pciDss = monthlyCharges?.pciDss;

  const vdFaceRate = visaRates?.debit?.face;
  const mcPhoneOnlineRate =
    mastercardRates?.credit?.online || mastercardRates?.credit?.moto;
  const vdFaceTotal = getTotalCardRatePercent(
    vdFaceRate,
    authorisationFee?.value
  );
  const mcPhoneOnlineTotal = getTotalCardRatePercent(
    mcPhoneOnlineRate,
    authorisationFee?.value
  );

  const details = {
    time: 'Depends on contract',
    brands: [
      'VISA',
      'Mastercard',
      'American Express',
      'JCB',
      'UPI',
      'Discover (Diners)',
    ],
  };

  return (
    <Document>
      <Page size='A4'>
        <Banner>
          <LloydsLogo allowDangerousPaths={true} src={IMAGES.logoUrlPNG} />
        </Banner>
        <ServiceName>
          {getPsrLabel('cardPaymentServicesDetails.journeyHeader.serviceName')}
        </ServiceName>
        <Title>
          {getPsrLabel('cardPaymentServicesDetails.journeyHeader.header')}
        </Title>
        <TextWrapper>
          {getPsrLabel('cardPaymentServicesDetails.journeyHeader.stepCounter')}
        </TextWrapper>
        <QuoteNumber>
          <Text>
            {getPsrLabel('cardPaymentServicesDetails.quoteReference.label')}{' '}
            {quoteReference}
          </Text>
        </QuoteNumber>

        <View>
          <PSRLogo allowDangerousPaths={true} src={IMAGES.psrLogoUrlPNG} />
          <TextWrapper>
            {getPsrLabel(
              'cardPaymentServicesDetails.outputHeader.headerText.text1'
            )}
          </TextWrapper>
          <TextWrapper>
            {getPsrLabel(
              'cardPaymentServicesDetails.outputHeader.headerText.text2'
            )}
          </TextWrapper>
          <TextWrapper>
            {getPsrLabel(
              'cardPaymentServicesDetails.outputHeader.headerText.text3'
            )}
          </TextWrapper>
        </View>
        <View>
          <View
            style={{
              padding: '12pt',
              border: '2pt solid #000',
              marginTop: '12pt',
            }}
          >
            <ThinBorder />
            <View style={{flexDirection: 'row', display: 'flex'}}>
              <Header style={{fontWeight: '700', margin: '2pt 0 5pt 5pt'}}>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.labelText'
                )}
              </Header>
              <Sup src='#Footnote-5'>
                {getPsrLabel(
                  'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.labelTextRef'
                )}
              </Sup>
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: '16pt',
              }}
            >
              <Table style={{width: '260pt', marginLeft: '20pt'}}>
                <Tr>
                  <Th style={{borderBottomWidth: 0, width: '79.5pt'}} />
                  <Th style={{textAlign: 'center', width: '148.5pt'}}>
                    {getPsrLabel(
                      'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardTypes.type1'
                    )}
                  </Th>
                </Tr>
                <Tr>
                  <Td style={{width: '79.5pt', textAlign: 'left'}}>
                    <Strong>
                      {getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.labelText'
                      )}
                    </Strong>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <TextWithSup
                      src='#Footnote-2'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category1Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category1'
                      )}
                    />
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F1F1F1'}}>
                    <TextWithSup
                      src='#Footnote-3'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category2Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category2'
                      )}
                    />
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <TextWithSup
                      src='#Footnote-4'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category3Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category3'
                      )}
                    />
                  </Td>
                </Tr>
                <Tr>
                  <Td style={{width: '79.5pt'}}>
                    <TextWithSup
                      src='#Footnote-6'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode1Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode1'
                      )}
                    />
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <Text>{mdFace}</Text>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F1F1F1'}}>
                    <Text>{mcFace}</Text>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <Text>{mbFace}</Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td style={{width: '79.5pt'}}>
                    <TextWithSup
                      src='#Footnote-7'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode2Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode2'
                      )}
                    />
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <Text>{mdPhoneOnline}</Text>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F1F1F1'}}>
                    <Text>{mcPhoneOnline}</Text>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <Text>{mbPhoneOnline}</Text>
                  </Td>
                </Tr>
              </Table>
              <Table style={{width: '260pt', marginLeft: '20pt'}}>
                <Tr>
                  <Th style={{borderBottomWidth: 0, width: '79.5pt'}} />
                  <Th style={{textAlign: 'center', width: '148.5pt'}}>
                    {getPsrLabel(
                      'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardTypes.type2'
                    )}
                  </Th>
                </Tr>
                <Tr>
                  <Td style={{width: '79.5pt', textAlign: 'left'}}>
                    <Strong>
                      {' '}
                      {getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.labelText'
                      )}
                    </Strong>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <TextWithSup
                      src='#Footnote-2'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category1Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category1'
                      )}
                    />
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F1F1F1'}}>
                    <TextWithSup
                      src='#Footnote-3'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category2Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category2'
                      )}
                    />
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <TextWithSup
                      src='#Footnote-4'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category3Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.cardsCategory.category3'
                      )}
                    />
                  </Td>
                </Tr>
                <Tr>
                  <Td style={{width: '79.5pt'}}>
                    <TextWithSup
                      src='#Footnote-6'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode1Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode1'
                      )}
                    />
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <Text>{vdFace}</Text>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F1F1F1'}}>
                    <Text>{vcFace}</Text>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <Text>{vbFace}</Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td style={{width: '79.5pt'}}>
                    <TextWithSup
                      src='#Footnote-7'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode2Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.cardPaymentsCharges.mode.mode2'
                      )}
                    />
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <Text>{vdPhoneOnline}</Text>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F1F1F1'}}>
                    <Text>{vcPhoneOnline}</Text>
                  </Td>
                  <Td style={{width: '49.5pt', backgroundColor: '#F9F9F9'}}>
                    <Text>{vbPhoneOnline}</Text>
                  </Td>
                </Tr>
              </Table>
            </View>

            <Header>
              {getPsrLabel(
                'cardPaymentServicesDetails.transactionCharges.additionalCharges.labelText'
              )}
            </Header>
            <Table
              style={{width: '252pt', marginLeft: '20pt', marginBottom: '29pt'}}
            >
              <Tr>
                <Td
                  style={{
                    width: '159pt',
                    borderWidth: '1pt',
                    textAlign: 'left',
                  }}
                >
                  <Text>
                    {' '}
                    {getPsrLabel(
                      'cardPaymentServicesDetails.transactionCharges.additionalCharges.feeLabel'
                    )}
                  </Text>
                </Td>
                <Td
                  style={{
                    width: '69pt',
                    borderWidth: '1pt',
                    borderLeft: 'none',
                    backgroundColor: '#F1F1F1',
                  }}
                >
                  <Strong> {getTotalPercent(authorisationFee?.value)}</Strong>
                </Td>
              </Tr>
            </Table>

            <Header>
              {getPsrLabel(
                'cardPaymentServicesDetails.transactionCharges.otherCharges.labelText'
              )}
            </Header>
            <View style={{display: 'flex', flexDirection: 'row'}}>
              <Table style={{width: '252pt', marginLeft: '20pt'}}>
                <Tr>
                  <Td
                    style={{
                      width: '159pt',
                      borderWidth: '1pt',
                      borderStyle: 'dotted',
                      textAlign: 'left',
                    }}
                  >
                    <Text>
                      {getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.otherCharges.feeLabel1'
                      )}
                    </Text>
                  </Td>
                  <Td
                    style={{
                      width: '69pt',
                      borderWidth: '1pt',
                      borderStyle: 'dotted',
                      borderLeft: 'none',
                      backgroundColor: '#F1F1F1',
                    }}
                  >
                    <Strong>
                      {getQuotationPrice(chargeBackFee?.value, true)}
                    </Strong>
                  </Td>
                </Tr>
              </Table>
              <Table style={{width: '252pt', marginLeft: '20pt'}}>
                <Tr>
                  <Td
                    style={{
                      width: '159pt',
                      borderWidth: '1pt',
                      borderStyle: 'dotted',
                      textAlign: 'left',
                    }}
                  >
                    <Text>
                      {' '}
                      {getPsrLabel(
                        'cardPaymentServicesDetails.transactionCharges.otherCharges.feeLabel2'
                      )}
                    </Text>
                  </Td>
                  <Td
                    style={{
                      width: '69pt',
                      borderWidth: '1pt',
                      borderStyle: 'dotted',
                      borderLeft: 'none',
                      backgroundColor: '#F1F1F1',
                    }}
                  >
                    <Strong>
                      {' '}
                      {getQuotationPrice(
                        revivalFee?.value,
                        revivalFee?.applicable
                      )}
                    </Strong>
                  </Td>
                </Tr>
              </Table>
            </View>
          </View>
          <View style={{marginBottom: '16pt', marginTop: '24pt'}}>
            <View>
              <Header style={{marginLeft: '0pt'}}>
                {getPsrLabel('cardPaymentServicesDetails.example.labelText')}
              </Header>

              <Paragraph style={{marginBottom: '9pt'}}>
                {getPsrLabel('pdfTemplate.title.customerMastercard')}{' '}
                <Strong>
                  {' '}
                  {getPsrLabel('pdfTemplate.title.personalDebit')}
                </Strong>{' '}
                {getPsrLabel('pdfTemplate.title.purchase')}
              </Paragraph>
              <Table>
                <Tr style={{borderTop: '#A1A1A1', borderTopWidth: '1pt'}}>
                  <Td
                    style={{
                      width: '33.33%',
                      backgroundColor: '#F1F1F1',
                      borderBottom: 'none',
                    }}
                  >
                    <Paragraph>
                      {' '}
                      <Strong>
                        {getPercent(getCardRatePercent(vdFaceRate))}
                      </Strong>{' '}
                      {'\n'}({`${vdFace} of ${getQuotationPrice(10.0, true)}`})
                    </Paragraph>
                  </Td>
                  <Td
                    style={{
                      width: '33.33%',
                      backgroundColor: '#F9F9F9',
                      borderBottom: 'none',
                    }}
                  >
                    <Paragraph>
                      {' '}
                      <Strong>
                        +{getTotalPercent(authorisationFee?.value)}
                      </Strong>{' '}
                      {'\n'} {getPsrLabel('pdfTemplate.title.feeSubText')}
                    </Paragraph>
                  </Td>
                  <Td
                    style={{
                      width: '33.33%',
                      backgroundColor: '#F1F1F1',
                      borderBottom: 'none',
                    }}
                  >
                    <Strong>={getPercent(vdFaceTotal)}</Strong>
                  </Td>
                </Tr>
              </Table>
            </View>
            <View style={{marginBottom: '16pt', marginTop: '24pt'}}>
              <Paragraph style={{marginBottom: '9pt'}}>
                {getPsrLabel('pdfTemplate.title.customerVisa')}{' '}
                <Strong>
                  {' '}
                  {getPsrLabel('pdfTemplate.title.personalCredit')}
                </Strong>
                {getPsrLabel('pdfTemplate.title.purchase')}
              </Paragraph>
              <Table>
                <Tr style={{borderTop: '#A1A1A1', borderTopWidth: '1pt'}}>
                  <Td
                    style={{
                      width: '33.33%',
                      backgroundColor: '#F1F1F1',
                      borderBottom: 'none',
                    }}
                  >
                    <Strong>
                      {getPercent(getCardRatePercent(mcPhoneOnlineRate))}
                    </Strong>
                    {'\n'}
                    <Paragraph>
                      ({`${mcPhoneOnline} of ${getQuotationPrice(10.0, true)}`})
                    </Paragraph>
                  </Td>
                  <Td
                    style={{
                      width: '33.33%',
                      backgroundColor: '#F9F9F9',
                      borderBottom: 'none',
                    }}
                  >
                    <Strong>+{getTotalPercent(authorisationFee?.value)}</Strong>
                    {'\n'}
                    <Paragraph>
                      {getPsrLabel('pdfTemplate.title.feeSubText')}
                    </Paragraph>
                  </Td>
                  <Td
                    style={{
                      width: '33.33%',
                      backgroundColor: '#F1F1F1',
                      borderBottom: 'none',
                    }}
                  >
                    <Strong>={getPercent(mcPhoneOnlineTotal)}</Strong>
                  </Td>
                </Tr>
              </Table>
            </View>

            <View
              break={true}
              style={{marginBottom: '16pt', marginTop: '20pt'}}
            >
              <Header style={{marginLeft: '0pt'}}>
                {' '}
                {getPsrLabel(
                  'cardPaymentServicesDetails.otherSection.monthlyCharges.labelText'
                )}
              </Header>
              <Table>
                <Tr style={{borderTop: '#A1A1A1', borderTopWidth: '1pt'}}>
                  <Td style={{width: '50%', textAlign: 'left'}}>
                    <Text>
                      {' '}
                      {getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label1'
                      )}
                    </Text>
                  </Td>
                  <Td style={{width: '50%', textAlign: 'right'}}>
                    <Text>
                      {' '}
                      {pos?.description ||
                        getPsrLabel('cardPaymentServicesDetails.notApplicable')}
                    </Text>
                  </Td>
                </Tr>
                <Tr style={{backgroundColor: '#F1F1F1'}}>
                  <Td style={{width: '50%', textAlign: 'left'}}>
                    <TextWithSup
                      src='#Footnote-8'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label2Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label2'
                      )}
                    />
                  </Td>
                  <Td style={{width: '50%', textAlign: 'right'}}>
                    <Text>
                      {' '}
                      {gateway?.applicable
                        ? `${getQuotationPrice(
                            gateway?.monthly?.value,
                            true
                          )} monthly + ${transactionValue} per transaction`
                        : getPsrLabel(
                            'cardPaymentServicesDetails.notApplicable'
                          )}
                    </Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td style={{width: '50%', textAlign: 'left'}}>
                    <TextWithSup
                      src='#Footnote-9'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label3Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label3'
                      )}
                    />
                  </Td>
                  <Td style={{width: '50%', textAlign: 'right'}}>
                    <Text>{`£${pciDss?.amount?.value || 0}`}</Text>
                  </Td>
                </Tr>
                <Tr style={{backgroundColor: '#F1F1F1'}}>
                  <Td style={{width: '50%', textAlign: 'left'}}>
                    <TextWithSup
                      src='#Footnote-10'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label4Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.monthlyCharges.labels.label4'
                      )}
                    />
                  </Td>
                  <Td style={{width: '50%', textAlign: 'right'}}>
                    <Text>{getQuotationPrice(mmsc?.value, true)}</Text>
                  </Td>
                </Tr>
              </Table>
            </View>
            <View>
              <Header style={{marginLeft: '0pt'}}>
                {' '}
                {getPsrLabel(
                  'cardPaymentServicesDetails.otherSection.otherDetails.labelText'
                )}
              </Header>
              <Table style={{margintop: '20pt', backgroundColor: '#F9F9F9'}}>
                <Tr style={{borderTop: '#A1A1A1', borderTopWidth: '1pt'}}>
                  <Td style={{width: '50%', textAlign: 'left'}}>
                    <TextWithSup
                      src='#Footnote-11'
                      sup={getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.otherDetails.labels.label1Ref'
                      )}
                      text={getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.otherDetails.labels.label1'
                      )}
                    />
                  </Td>
                  <Td style={{width: '50%', textAlign: 'right'}}>
                    <Text>{details.time}</Text>
                  </Td>
                </Tr>
                <Tr>
                  <Td style={{width: '50%', textAlign: 'left'}}>
                    <Text>
                      {' '}
                      {getPsrLabel(
                        'cardPaymentServicesDetails.otherSection.otherDetails.labels.label2'
                      )}
                    </Text>
                  </Td>
                  <Td style={{width: '50%', textAlign: 'right'}}>
                    {details.brands.map((brand) => (
                      <Text key={brand}>{brand}</Text>
                    ))}
                  </Td>
                </Tr>
              </Table>
            </View>
          </View>
          <View style={{marginTop: '18pt'}}>
            {getPsrLabel('cardPaymentServicesDetails.footnotes').map(
              ({description, refid}, index) => (
                <Paragraph
                  id={`Footnote-${index + 1}`}
                  key={`Footnote-${refid}`}
                >
                  {index + 1}. {description}
                </Paragraph>
              )
            )}
          </View>
          <View break={true}>
            <Strong>
              {getPsrLabel('pdfTemplate.callToActionAplication.Title')}
            </Strong>
            <View
              style={{
                padding: '18pt',
                backgroundColor: '#006A4D',
                marginBottom: '27pt',
                position: 'relative',
                marginTop: '27pt',
              }}
            >
              <PhoneIcon allowDangerousPaths={true} src={IMAGES.phonIconUrl} />;
              <View
                style={{
                  borderBottom: '0.5pt solid #fff',
                  marginBottom: '12pt',
                  paddingBottom: '12pt',
                }}
              >
                <Question>
                  {getPsrLabel(
                    'pdfTemplate.callToActionAplication.row1.question'
                  )}
                </Question>
                <Phone>
                  {getPsrLabel('pdfTemplate.callToActionAplication.row1.phone')}{' '}
                </Phone>
                <Paragraph style={{color: '#fff'}}>
                  {getPsrLabel(
                    'pdfTemplate.callToActionAplication.row1.information'
                  )}
                </Paragraph>
              </View>
              <View>
                <Question>
                  {getPsrLabel(
                    'pdfTemplate.callToActionAplication.row2.question'
                  )}
                </Question>
                <Phone>
                  {getPsrLabel('pdfTemplate.callToActionAplication.row2.phone')}{' '}
                </Phone>
                <Paragraph style={{color: '#fff', marginBottom: '0pt'}}>
                  {getPsrLabel(
                    'pdfTemplate.callToActionAplication.row2.information'
                  )}
                </Paragraph>
              </View>
            </View>
            <CallToActionWrapper>
              <CallToActionHeader>
                <MouseIcon
                  allowDangerousPaths={true}
                  src={IMAGES.mouseIconUrl}
                />{' '}
                <Paragraph style={{fontSize: '18pt'}}>
                  {getPsrLabel(
                    'cardPaymentServicesDetails.callToActionGuideLines.titleText'
                  )}
                </Paragraph>
              </CallToActionHeader>
              {getPsrLabel(
                'cardPaymentServicesDetails.callToActionGuideLines.criteriaList'
              ).map(({description, id}) => (
                <ListItem key={`Footnote-${id}`} text={description} />
              ))}
              <Paragraph style={{marginTop: '5pt'}}>
                {getPsrLabel(
                  'cardPaymentServicesDetails.callToActionGuideLines.note'
                )}
              </Paragraph>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  marginTop: '5pt',
                }}
              >
                <ApplyNowCTA
                  label={getPsrLabel(
                    'cardPaymentServicesDetails.callToActionGuideLines.ctaLabel'
                  )}
                  src={getPsrLabel(
                    'cardPaymentServicesDetails.callToActionGuideLines.applicationJourneyLink'
                  )}
                />
                <ClockIcon
                  allowDangerousPaths={true}
                  src={IMAGES.clockIconUrl}
                />
                <Paragraph style={{fontSize: '12pt', marginTop: '5pt'}}>
                  {getPsrLabel(
                    'cardPaymentServicesDetails.callToActionGuideLines.clockText'
                  )}
                </Paragraph>
              </View>
            </CallToActionWrapper>

            <Paragraph>
              {getPsrLabel('footer.cardnet')}
              <Link
                src={getPsrLabel('footer.lgbLink')}
                style={{color: '#024731'}}
              >
                {' '}
                {getPsrLabel('footer.lgbLink')}{' '}
              </Link>
              {getPsrLabel('footer.subText')}
              <Link
                src={getPsrLabel('footer.fcaLink')}
                style={{color: '#024731'}}
              >
                {' '}
                {getPsrLabel('footer.fcaLink')}{' '}
              </Link>
            </Paragraph>
          </View>
        </View>
      </Page>
    </Document>
  );
});

export default QuoteDocument;
